import React, { useState, useEffect } from 'react';
import { 
  Database, 
  CheckCircle2, 
  Circle, 
  ChevronDown, 
  ChevronRight,
  Search,
  Filter,
  Save,
  Download,
  FileText,
  AlertTriangle,
  Info,
  RefreshCw,
  Target,
  Leaf,
  Users,
  Shield,
  Building2,
  BarChart3,
  Globe,
  Zap,
  Upload,
  Link2,
  Clock,
  ArrowRight,
  XCircle,
  AlertCircle,
  TrendingUp,
  Eye,
  FileCheck,
  Briefcase,
  Lightbulb,
  Play,
  ChevronUp,
  Sparkles,
  Calendar,
  Lock,
  Unlock,
  ExternalLink,
  User,
  UserCircle,
  Ban,
  Pause,
  RotateCcw,
  X,
  Rocket,
  Layers,
  GitBranch,
  Activity,
  Gauge,
  History,
  List,
  PanelRightOpen,
  FileWarning,
  ClipboardCheck,
  Timer,
  Award
} from 'lucide-react';
import { apiFetch } from '../services/api_service';
import { useAIQuality, useAIDraft } from '../hooks/ai_module_hooks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role metadata for display
const ROLE_METADATA = {
  admin: { label: 'Administrator', color: 'red', icon: Shield },
  compliance_manager: { label: 'Compliance Manager', color: 'purple', icon: CheckCircle2 },
  data_analyst: { label: 'Data Analyst', color: 'blue', icon: Database },
  legal_counsel: { label: 'Legal Counsel', color: 'amber', icon: FileText },
  policy_adviser: { label: 'Policy Adviser', color: 'teal', icon: FileCheck },
  auditor: { label: 'Auditor', color: 'slate', icon: Eye },
  viewer: { label: 'Viewer', color: 'gray', icon: User },
  unassigned: { label: 'Unassigned', color: 'gray', icon: UserCircle }
};

// Standard icons mapping
const standardIcons = {
  'ESRS 2': Globe,
  'ESRS E1': Leaf,
  'ESRS E2': AlertTriangle,
  'ESRS E3': Database,
  'ESRS E4': Leaf,
  'ESRS E5': RefreshCw,
  'ESRS S1': Users,
  'ESRS S2': Users,
  'ESRS S3': Building2,
  'ESRS S4': Users,
  'ESRS G1': Shield,
  'ESRS G2': Shield
};

// Category colors
const categoryColors = {
  environmental: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', accent: 'emerald' },
  social: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', accent: 'blue' },
  governance: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', accent: 'purple' },
  general: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', accent: 'slate' }
};

// ============================================================================
// LAYER 1: Executive Readiness Bar with Clickable Metrics
// ============================================================================
const ExecutiveReadinessBar = ({ stats, timeToReady, claimsBreakdown, onMetricClick }) => {
  const getHealthStatus = (score) => {
    if (score >= 80) return { label: 'Defensible', color: 'emerald', icon: Shield };
    if (score >= 50) return { label: 'Moderate', color: 'amber', icon: AlertTriangle };
    return { label: 'Weak', color: 'red', icon: XCircle };
  };
  
  const health = getHealthStatus(stats.overallScore);
  const HealthIcon = health.icon;
  const [expandedMetric, setExpandedMetric] = useState(null);

  const handleMetricClick = (metricKey) => {
    setExpandedMetric(expandedMetric === metricKey ? null : metricKey);
    if (onMetricClick) onMetricClick(metricKey);
  };

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Target size={24} className="text-emerald-400" />
            CSRD Readiness Score
          </h2>
          <p className="text-slate-400 text-sm mt-1">Real-time compliance assessment</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 rounded-full bg-${health.color}-500/20 border border-${health.color}-500/50 flex items-center gap-2`}>
            <HealthIcon size={16} className={`text-${health.color}-400`} />
            <span className={`text-${health.color}-300 font-semibold`}>Disclosure Health: {health.label}</span>
          </div>
        </div>
      </div>
      
      {/* Main Score */}
      <div className="grid grid-cols-5 gap-6">
        {/* Overall Readiness - Large */}
        <div className="col-span-1 text-center">
          <div className="relative inline-flex items-center justify-center">
            <svg className="w-28 h-28 transform -rotate-90">
              <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="8" fill="none" className="text-slate-700" />
              <circle 
                cx="56" cy="56" r="48" 
                stroke="currentColor" 
                strokeWidth="8" 
                fill="none" 
                strokeDasharray={`${stats.overallScore * 3.01} 301`}
                className={`text-${stats.overallScore >= 80 ? 'emerald' : stats.overallScore >= 50 ? 'amber' : 'red'}-400`}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold">{stats.overallScore}%</span>
              <span className="text-xs text-slate-400">Overall</span>
            </div>
          </div>
        </div>
        
        {/* Breakdown Metrics - Clickable */}
        <div className="col-span-4 grid grid-cols-4 gap-4">
          {[
            { key: 'data', label: 'Data Coverage', value: stats.dataCoverage, icon: Database, desc: 'Data points filled' },
            { key: 'evidence', label: 'Evidence Coverage', value: stats.evidenceCoverage, icon: FileCheck, desc: 'With supporting docs' },
            { key: 'claims', label: 'Claims Defensible', value: stats.claimsDefensible, icon: Shield, desc: 'Audit-ready statements', hasBreakdown: true },
            { key: 'audit', label: 'Audit Ready', value: stats.auditReady ? 'Yes' : 'No', icon: Briefcase, isBoolean: true }
          ].map((metric) => {
            const Icon = metric.icon;
            const isExpanded = expandedMetric === metric.key;
            return (
              <div key={metric.key} className="relative">
                <button 
                  onClick={() => metric.hasBreakdown && handleMetricClick(metric.key)}
                  className={`w-full bg-slate-800/50 rounded-xl p-4 border transition-all text-left ${
                    metric.hasBreakdown ? 'border-slate-700 hover:border-slate-500 cursor-pointer' : 'border-slate-700'
                  } ${isExpanded ? 'ring-2 ring-amber-500/50' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className="text-slate-400" />
                    <span className="text-xs text-slate-400">{metric.label}</span>
                    {metric.hasBreakdown && (
                      <Info size={12} className="text-slate-500 ml-auto" />
                    )}
                  </div>
                  {metric.isBoolean ? (
                    <div className={`text-2xl font-bold ${metric.value === 'Yes' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {metric.value}
                    </div>
                  ) : (
                    <>
                      <div className="text-2xl font-bold text-white">{metric.value}%</div>
                      <div className="w-full h-1.5 bg-slate-700 rounded-full mt-2 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            metric.value >= 80 ? 'bg-emerald-400' : metric.value >= 50 ? 'bg-amber-400' : 'bg-red-400'
                          }`}
                          style={{ width: `${metric.value}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{metric.desc}</p>
                    </>
                  )}
                </button>
                
                {/* Claims Breakdown Tooltip */}
                {metric.key === 'claims' && isExpanded && claimsBreakdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-600 rounded-lg p-4 z-50 shadow-xl">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-white">Why {stats.claimsDefensible}%?</span>
                      <button onClick={() => setExpandedMetric(null)} className="text-slate-400 hover:text-white">
                        <X size={14} />
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Drafted:</span>
                        <span className="text-amber-400">{claimsBreakdown.breakdown?.drafted || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Pending Approval:</span>
                        <span className="text-amber-400">{claimsBreakdown.breakdown?.pending_approval || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Approved:</span>
                        <span className="text-emerald-400">{claimsBreakdown.breakdown?.approved || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Locked:</span>
                        <span className="text-emerald-400">{claimsBreakdown.breakdown?.locked || 0}</span>
                      </div>
                      <div className="border-t border-slate-700 pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Missing Evidence:</span>
                          <span className="text-red-400">{claimsBreakdown.defensibility?.missing_evidence_count || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Missing Approvals:</span>
                          <span className="text-red-400">{claimsBreakdown.defensibility?.missing_approvals_count || 0}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-amber-400 mt-3 flex items-center gap-1">
                      <Lightbulb size={12} />
                      {claimsBreakdown.defensibility?.recommendation || 'Get claims approved to increase score'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Time to Audit Ready */}
      <div className="mt-6 pt-4 border-t border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock size={18} className="text-amber-400" />
          <div>
            <span className="text-sm text-slate-400">Estimated time to audit-ready:</span>
            <span className="ml-2 text-lg font-bold text-amber-300">{timeToReady.weeks} weeks</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle size={14} className="text-amber-400" />
          <span className="text-slate-400">Biggest blocker:</span>
          <span className="text-amber-300 font-medium">{timeToReady.blocker}</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// LAYER 2: Action-Oriented Gap Engine with Segmentation & Ownership
// ============================================================================
const GapEngine = ({ gapAnalysis, onGapClick }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // all, blocking, high, medium
  
  const summary = gapAnalysis?.summary || { blocking_audit: 0, high_priority: 0, medium_priority: 0, low_priority: 0, overdue_count: 0 };
  const gaps = gapAnalysis?.gaps || { blocking_audit: [], high_priority: [], medium_priority: [], low_priority: [] };
  
  // Get filtered gaps based on active filter
  const getFilteredGaps = () => {
    if (activeFilter === 'blocking') return gaps.blocking_audit;
    if (activeFilter === 'high') return gaps.high_priority;
    if (activeFilter === 'medium') return gaps.medium_priority;
    return [...gaps.blocking_audit, ...gaps.high_priority, ...gaps.medium_priority.slice(0, 3)];
  };
  
  const filteredGaps = getFilteredGaps().slice(0, 8);
  
  const getRoleIcon = (role) => {
    const meta = ROLE_METADATA[role] || ROLE_METADATA.unassigned;
    return meta.icon;
  };
  
  const getRoleLabel = (role) => {
    const meta = ROLE_METADATA[role] || ROLE_METADATA.unassigned;
    return meta.label;
  };

  return (
    <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-xl border border-red-200 p-4 mb-6">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <h3 className="text-lg font-bold text-red-900 flex items-center gap-2">
          <AlertCircle size={20} className="text-red-600" />
          What Needs Attention Now
        </h3>
        {isExpanded ? <ChevronUp size={20} className="text-red-600" /> : <ChevronDown size={20} className="text-red-600" />}
      </button>
      
      {/* Segmented Gap Counts */}
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button
          onClick={() => setActiveFilter('blocking')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
            activeFilter === 'blocking' 
              ? 'bg-red-600 text-white' 
              : 'bg-red-100 text-red-700 hover:bg-red-200'
          }`}
        >
          <Ban size={14} />
          {summary.blocking_audit} blocking audit
        </button>
        <button
          onClick={() => setActiveFilter('high')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
            activeFilter === 'high' 
              ? 'bg-amber-600 text-white' 
              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
          }`}
        >
          <AlertTriangle size={14} />
          {summary.high_priority} high priority
        </button>
        <button
          onClick={() => setActiveFilter('medium')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
            activeFilter === 'medium' 
              ? 'bg-blue-600 text-white' 
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          <Circle size={14} />
          {summary.medium_priority} medium
        </button>
        {summary.overdue_count > 0 && (
          <span className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-1">
            <Clock size={14} />
            {summary.overdue_count} overdue
          </span>
        )}
        <button
          onClick={() => setActiveFilter('all')}
          className={`ml-auto px-3 py-1.5 text-sm font-medium transition-all ${
            activeFilter === 'all' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Show all
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-2">
          {filteredGaps.map((gap, idx) => {
            const RoleIcon = getRoleIcon(gap.owner_role);
            const isBlocking = gap.blocks_audit;
            
            return (
              <button
                key={idx}
                onClick={() => onGapClick(gap)}
                className="w-full flex items-center gap-3 p-3 bg-white rounded-lg border border-red-100 hover:border-red-300 hover:shadow-sm transition-all text-left group"
              >
                {/* Risk Indicator */}
                <div className={`w-2 h-10 rounded-full flex-shrink-0 ${
                  isBlocking ? 'bg-red-500' : gap.risk_level === 'high' ? 'bg-amber-500' : 'bg-blue-400'
                }`} />
                
                {/* Gap Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900 group-hover:text-red-700">{gap.standard_code}</p>
                    {isBlocking && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">BLOCKS AUDIT</span>
                    )}
                    {gap.overdue && (
                      <span className="px-1.5 py-0.5 bg-red-600 text-white rounded text-xs font-medium">OVERDUE</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                    <span>{gap.missing_data_points || 0} missing data</span>
                    <span>{gap.missing_evidence || 0} missing evidence</span>
                    {gap.cross_framework_impact?.length > 1 && (
                      <span className="text-indigo-600">Impacts {gap.cross_framework_impact.length} frameworks</span>
                    )}
                  </div>
                </div>
                
                {/* Owner */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
                  <RoleIcon size={14} className="text-slate-500" />
                  <span className="text-xs text-slate-600">{getRoleLabel(gap.owner_role)}</span>
                </div>
                
                {/* Due Date */}
                {gap.due_date && (
                  <div className={`text-xs px-2 py-1 rounded ${gap.overdue ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                    {new Date(gap.due_date).toLocaleDateString()}
                  </div>
                )}
                
                <ArrowRight size={14} className="text-slate-400 group-hover:text-red-600 transition-colors flex-shrink-0" />
              </button>
            );
          })}
          
          {(summary.blocking_audit + summary.high_priority + summary.medium_priority) > 8 && (
            <button className="w-full py-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center justify-center gap-1">
              View all {summary.blocking_audit + summary.high_priority + summary.medium_priority} gaps <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LAYER 3: Operational Pipeline View with Status Chips & Owner Column
// ============================================================================
const PipelineView = ({ topics, riskAssessment, ownership, onTopicClick, onStageClick }) => {
  const stages = ['Upload', 'Map', 'Evidence', 'Claim', 'Audit'];
  const [auditBlockersOnly, setAuditBlockersOnly] = useState(false);
  
  const getStageIcon = (stage) => {
    switch(stage) {
      case 'Upload': return Upload;
      case 'Map': return Link2;
      case 'Evidence': return FileCheck;
      case 'Claim': return FileText;
      case 'Audit': return Briefcase;
      default: return Circle;
    }
  };
  
  // Status Chip component - replaces empty circles
  const StatusChip = ({ status, count, missing, stage, topic, onStageClick: handleStageClick }) => {
    const getChipStyle = () => {
      if (status === 'complete') return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Complete' };
      if (status === 'in_progress') return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'In Progress' };
      if (status === 'blocked') return { bg: 'bg-red-100', text: 'text-red-700', label: 'Blocked' };
      if (status === 'needs_review') return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Needs Review' };
      return { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Not Started' };
    };
    
    const style = getChipStyle();
    
    return (
      <button 
        onClick={(e) => {
          e.stopPropagation();
          if (handleStageClick) handleStageClick(topic, stage);
        }}
        className={`px-2 py-1 rounded text-xs font-medium ${style.bg} ${style.text} hover:opacity-80 transition-opacity flex items-center gap-1 cursor-pointer`}
        title={`${style.label}${missing > 0 ? ` - ${missing} missing` : ''}`}
        data-testid={`stage-chip-${topic?.code}-${stage}`}
      >
        {status === 'complete' && <CheckCircle2 size={10} />}
        {status === 'in_progress' && <Clock size={10} />}
        {status === 'blocked' && <Ban size={10} />}
        {status === 'needs_review' && <Eye size={10} />}
        {status === 'not_started' && <Circle size={10} />}
        {missing > 0 && <span className="ml-0.5">{missing}</span>}
      </button>
    );
  };
  
  // Get risk level indicator
  const getRiskIndicator = (standardCode) => {
    const risk = riskAssessment?.[standardCode];
    if (!risk) return null;
    
    const level = risk.risk_level;
    if (level === 'high') return <span title="High Risk" className="w-2 h-2 rounded-full bg-red-500" />;
    if (level === 'medium') return <span title="Medium Risk" className="w-2 h-2 rounded-full bg-amber-500" />;
    return <span title="Low Risk" className="w-2 h-2 rounded-full bg-emerald-500" />;
  };
  
  // Filter topics if audit blockers only
  const filteredTopics = auditBlockersOnly 
    ? topics.filter(t => riskAssessment?.[t.code.replace(' ', '_')]?.blocks_audit)
    : topics;
  
  // Get owner info for a topic
  const getOwnerInfo = (topicCode) => {
    const code = topicCode.replace(' ', '_');
    const ownerData = ownership?.[code];
    const role = ownerData?.owner_role || 'unassigned';
    const meta = ROLE_METADATA[role] || ROLE_METADATA.unassigned;
    return { role, label: meta.label, Icon: meta.icon };
  };
  
  // Get cross-framework badges
  const getFrameworkBadges = (topicCode) => {
    const code = topicCode.replace(' ', '_');
    const risk = riskAssessment?.[code];
    return risk?.cross_framework_impact || [];
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Play size={20} className="text-emerald-600" />
          Operational Pipeline
        </h3>
        <div className="flex items-center gap-4">
          {/* Audit Blockers Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={auditBlockersOnly}
              onChange={(e) => setAuditBlockersOnly(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-xs font-medium text-red-700">Audit Blockers Only</span>
          </label>
          <div className="flex items-center gap-3 text-xs border-l border-slate-200 pl-4">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> High Risk</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Medium</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Low</span>
          </div>
        </div>
      </div>
      
      {/* Pipeline Header - Added Owner & Risk columns */}
      <div className="grid grid-cols-8 gap-2 mb-2 px-2">
        <div className="text-xs font-semibold text-slate-500">Topic</div>
        <div className="text-xs font-semibold text-slate-500">Owner</div>
        {stages.map((stage) => {
          const Icon = getStageIcon(stage);
          return (
            <div key={stage} className="text-center">
              <div className="flex items-center justify-center gap-1 text-xs font-semibold text-slate-600">
                <Icon size={12} />
                {stage}
              </div>
            </div>
          );
        })}
        <div className="text-xs font-semibold text-slate-500 text-center">Risk</div>
      </div>
      
      {/* Pipeline Rows with Status Chips, Owner, Risk */}
      <div className="space-y-1">
        {filteredTopics.map((topic) => {
          const Icon = standardIcons[topic.code] || Database;
          const colors = categoryColors[topic.category] || categoryColors.general;
          const ownerInfo = getOwnerInfo(topic.code);
          const OwnerIcon = ownerInfo.Icon;
          const frameworks = getFrameworkBadges(topic.code);
          const topicRisk = riskAssessment?.[topic.code.replace(' ', '_')];
          
          return (
            <div
              key={topic.code}
              onClick={() => onTopicClick(topic)}
              className="w-full grid grid-cols-8 gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors group items-center cursor-pointer"
            >
              {/* Topic */}
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded flex items-center justify-center ${colors.bg}`}>
                  <Icon size={14} className={colors.text} />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium text-slate-900 group-hover:text-emerald-700">{topic.code}</span>
                  {frameworks.length > 1 && (
                    <div className="flex gap-1 mt-0.5">
                      {frameworks.slice(0, 3).map((fw, i) => (
                        <span key={i} className="text-[9px] px-1 bg-indigo-50 text-indigo-600 rounded">{fw}</span>
                      ))}
                      {frameworks.length > 3 && (
                        <span className="text-[9px] text-indigo-500">+{frameworks.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Owner */}
              <div className="flex items-center gap-1">
                <OwnerIcon size={12} className="text-slate-400" />
                <span className="text-xs text-slate-600 truncate">{ownerInfo.label.split(' ')[0]}</span>
              </div>
              
              {/* Pipeline Stages with Status Chips */}
              {stages.map((stage) => {
                const stageStatus = topic.stages?.[stage.toLowerCase()] || 'not_started';
                const stageData = topicRisk || {};
                let missing = 0;
                if (stage === 'Evidence') missing = 3 - Math.min(3, stageData.evidence_count || 0);
                if (stage === 'Claim') missing = stageData.claims_count === 0 ? 1 : 0;
                
                return (
                  <div key={stage} className="flex items-center justify-center">
                    <StatusChip 
                      status={stageStatus} 
                      count={0}
                      missing={missing}
                      stage={stage}
                      topic={topic}
                      onStageClick={onStageClick}
                    />
                  </div>
                );
              })}
              
              {/* Risk Indicator */}
              <div className="flex items-center justify-center gap-1">
                {getRiskIndicator(topic.code.replace(' ', '_'))}
                {topicRisk?.blocks_audit && (
                  <Ban size={12} className="text-red-500" title="Blocks Audit" />
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {auditBlockersOnly && filteredTopics.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <CheckCircle2 size={32} className="mx-auto mb-2 text-emerald-500" />
          <p className="text-sm">No audit blockers! All critical items addressed.</p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LAYER 4: Autopilot Suggestions
// ============================================================================
const AutopilotSuggestions = ({ suggestions, onAccept, onDismiss }) => {
  if (!suggestions || suggestions.length === 0) return null;
  
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={20} className="text-indigo-600" />
        <h3 className="font-semibold text-indigo-900">Autopilot Suggestions</h3>
        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">AI-Powered</span>
      </div>
      
      <div className="space-y-2">
        {suggestions.map((suggestion, idx) => (
          <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-indigo-100">
            <Lightbulb size={18} className="text-indigo-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-slate-900">{suggestion.message}</p>
              <p className="text-xs text-slate-500 mt-0.5">{suggestion.detail}</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => onAccept(suggestion)}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700"
              >
                Apply Now
              </button>
              <button 
                onClick={() => onDismiss(suggestion)}
                className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-xs hover:bg-slate-50"
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 2: Stage Drawer - Clickable Pipeline Stages Side Drawer
// ============================================================================
const StageDrawer = ({ isOpen, onClose, topic, stage, riskData, ownership, evidence, claims, onAction }) => {
  const [checkedItems, setCheckedItems] = useState({});
  
  // Reset checked items when drawer opens with new topic/stage
  useEffect(() => {
    if (isOpen && topic && stage) {
      // Initialize checked items based on existing data
      const topicCode = topic.code?.replace(' ', '_');
      const topicEvidence = evidence?.filter(e => e.standard_code === topic.code || e.standard_code === topicCode) || [];
      const topicClaims = claims?.filter(c => c.standard_code === topic.code || c.standard_code === topicCode) || [];
      
      const initialChecked = {};
      // Auto-check items based on existing evidence/claims
      if (stage === 'Evidence' && topicEvidence.length > 0) {
        initialChecked[0] = true; // Source documentation
        if (topicEvidence.some(e => e.evidence_type === 'third_party_verification')) {
          initialChecked[1] = true;
        }
        if (topicEvidence.length >= 2) {
          initialChecked[2] = true;
        }
      }
      if (stage === 'Claim' && topicClaims.length > 0) {
        initialChecked[0] = true; // Draft disclosure text
        if (topicClaims.some(c => c.status === 'pending_approval' || c.status === 'approved')) {
          initialChecked[1] = true; // Legal review
        }
        if (topicClaims.some(c => c.status === 'approved' || c.status === 'locked')) {
          initialChecked[2] = true; // Management approval
        }
      }
      setCheckedItems(initialChecked);
    }
  }, [isOpen, topic, stage, evidence, claims]);
  
  if (!isOpen || !topic || !stage) return null;
  
  const stageInfo = {
    Upload: {
      title: 'Data Upload',
      icon: Upload,
      description: 'Upload source files containing ESG data',
      requiredItems: ['Annual sustainability report', 'Carbon footprint calculations', 'Energy consumption data'],
      color: 'blue'
    },
    Map: {
      title: 'Data Mapping',
      icon: Link2,
      description: 'Map uploaded data to ESRS data points',
      requiredItems: ['Data point assignments', 'Unit conversions', 'Boundary definitions'],
      color: 'purple'
    },
    Evidence: {
      title: 'Evidence Attachment',
      icon: FileCheck,
      description: 'Attach supporting documents and verifications',
      requiredItems: ['Source documentation', 'Third-party verification', 'Calculation methodology'],
      color: 'emerald'
    },
    Claim: {
      title: 'Disclosure Claims',
      icon: FileText,
      description: 'Generate and approve disclosure statements',
      requiredItems: ['Draft disclosure text', 'Legal review', 'Management approval'],
      color: 'amber'
    },
    Audit: {
      title: 'Audit Preparation',
      icon: Briefcase,
      description: 'Prepare audit-ready documentation package',
      requiredItems: ['Evidence linkage', 'Approval chain', 'Version history'],
      color: 'slate'
    }
  };
  
  const info = stageInfo[stage] || stageInfo.Upload;
  const StageIcon = info.icon;
  const topicCode = topic.code?.replace(' ', '_');
  const ownerData = ownership?.[topicCode] || {};
  const topicRisk = riskData?.[topicCode] || {};
  
  // Filter evidence/claims for this topic
  const topicEvidence = evidence?.filter(e => e.standard_code === topic.code || e.standard_code === topicCode) || [];
  const topicClaims = claims?.filter(c => c.standard_code === topic.code || c.standard_code === topicCode) || [];
  
  // Calculate stage status
  const getStageStatus = () => {
    if (stage === 'Evidence') {
      if (topicEvidence.length >= 3) return { status: 'complete', label: 'Complete', color: 'emerald' };
      if (topicEvidence.length > 0) return { status: 'in_progress', label: 'In Progress', color: 'amber' };
      return { status: 'not_started', label: 'Not Started', color: 'slate' };
    }
    if (stage === 'Claim') {
      const approved = topicClaims.filter(c => c.status === 'approved' || c.status === 'locked').length;
      if (approved > 0) return { status: 'complete', label: 'Complete', color: 'emerald' };
      if (topicClaims.length > 0) return { status: 'in_progress', label: 'In Progress', color: 'amber' };
      return { status: 'not_started', label: 'Not Started', color: 'slate' };
    }
    return { status: 'not_started', label: 'Not Started', color: 'slate' };
  };
  
  const stageStatus = getStageStatus();
  
  // Toggle checkbox
  const toggleItem = (idx) => {
    setCheckedItems(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  
  // Calculate progress
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalItems = info.requiredItems.length;
  const progressPercent = Math.round((checkedCount / totalItems) * 100);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30" onClick={onClose} />
      
      {/* Drawer */}
      <div className="w-[480px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className={`px-6 py-4 bg-gradient-to-r from-${info.color}-50 to-${info.color}-100 border-b border-${info.color}-200`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-${info.color}-100 flex items-center justify-center`}>
                <StageIcon size={20} className={`text-${info.color}-600`} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{info.title}</h3>
                <p className="text-sm text-slate-600">{topic.code}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <X size={20} className="text-slate-500" />
            </button>
          </div>
          
          {/* Status Badge */}
          <div className="mt-3 flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${stageStatus.color}-100 text-${stageStatus.color}-700`}>
              {stageStatus.label}
            </span>
            {topicRisk.blocks_audit && (
              <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium flex items-center gap-1">
                <Ban size={12} /> Blocks Audit
              </span>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description */}
          <p className="text-sm text-slate-600">{info.description}</p>
          
          {/* Owner & Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <User size={12} /> Owner
              </div>
              <p className="font-medium text-slate-900">
                {ROLE_METADATA[ownerData.owner_role]?.label || 'Unassigned'}
              </p>
              {ownerData.owner_email && (
                <p className="text-xs text-slate-500 mt-0.5">{ownerData.owner_email}</p>
              )}
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <Calendar size={12} /> Due Date
              </div>
              <p className={`font-medium ${ownerData.overdue ? 'text-red-600' : 'text-slate-900'}`}>
                {ownerData.due_date ? new Date(ownerData.due_date).toLocaleDateString() : 'Not set'}
              </p>
              {ownerData.overdue && (
                <p className="text-xs text-red-500 mt-0.5">Overdue!</p>
              )}
            </div>
          </div>
          
          {/* Required Items Checklist - CLICKABLE */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <ClipboardCheck size={16} /> Required Items
              </h4>
              <span className="text-xs text-slate-500">{checkedCount}/{totalItems} complete</span>
            </div>
            
            {/* Progress bar */}
            <div className="w-full h-2 bg-slate-100 rounded-full mb-3 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  progressPercent === 100 ? 'bg-emerald-500' : progressPercent > 0 ? 'bg-amber-500' : 'bg-slate-200'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            
            <div className="space-y-2">
              {info.requiredItems.map((item, idx) => {
                const isChecked = checkedItems[idx] || false;
                return (
                  <button
                    key={idx}
                    onClick={() => toggleItem(idx)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                      isChecked 
                        ? 'bg-emerald-50 border-emerald-300 hover:border-emerald-400' 
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      isChecked 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-slate-300 hover:border-slate-400'
                    }`}>
                      {isChecked && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                    <span className={`text-sm flex-1 text-left ${isChecked ? 'text-emerald-900 font-medium' : 'text-slate-700'}`}>
                      {item}
                    </span>
                    {isChecked && (
                      <span className="text-xs text-emerald-600 font-medium">Done</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Uploaded Documents (for Evidence stage) */}
          {stage === 'Evidence' && topicEvidence.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileCheck size={16} /> Uploaded Documents ({topicEvidence.length})
              </h4>
              <div className="space-y-2">
                {topicEvidence.map((ev, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <FileText size={16} className="text-emerald-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{ev.file_name}</p>
                      <p className="text-xs text-slate-500">{ev.evidence_type}</p>
                    </div>
                    <CheckCircle2 size={16} className="text-emerald-600" />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Claims (for Claim stage) */}
          {stage === 'Claim' && topicClaims.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileText size={16} /> Disclosure Claims ({topicClaims.length})
              </h4>
              <div className="space-y-2">
                {topicClaims.map((claim, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    claim.status === 'approved' || claim.status === 'locked' 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        claim.status === 'approved' || claim.status === 'locked'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {claim.status?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2">{claim.claim_text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Missing Items */}
          {stageStatus.status !== 'complete' && (
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <FileWarning size={16} /> Missing Items
              </h4>
              <ul className="space-y-1">
                {stage === 'Evidence' && topicEvidence.length < 3 && (
                  <li className="text-sm text-amber-800">• {3 - topicEvidence.length} more evidence documents needed</li>
                )}
                {stage === 'Claim' && topicClaims.length === 0 && (
                  <li className="text-sm text-amber-800">• No disclosure claims generated yet</li>
                )}
                {stage === 'Claim' && topicClaims.length > 0 && !topicClaims.some(c => c.status === 'approved') && (
                  <li className="text-sm text-amber-800">• Claims pending approval</li>
                )}
              </ul>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center gap-3">
          <button 
            onClick={() => onAction && onAction('resolve', topic, stage)}
            className={`flex-1 px-4 py-2.5 bg-${info.color}-600 text-white rounded-lg text-sm font-medium hover:bg-${info.color}-700 flex items-center justify-center gap-2`}
          >
            <Play size={16} />
            {stage === 'Evidence' ? 'Upload Evidence' : stage === 'Claim' ? 'Generate Claim' : 'Start Stage'}
          </button>
          <button 
            onClick={onClose}
            className="px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 2: 3-Band Split View for All Disclosures
// ============================================================================
const ThreeBandView = ({ standards, riskAssessment, ownership, onTopicClick }) => {
  const [expandedBands, setExpandedBands] = useState({ band1: true, band2: false, band3: false });
  
  // Categorize standards into 3 bands
  const categorizeStandards = () => {
    const band1 = []; // Mandatory & Blocking
    const band2 = []; // Mandatory but Non-Blocking
    const band3 = []; // Voluntary
    
    standards.forEach(std => {
      const code = std.code?.replace(' ', '_');
      const risk = riskAssessment?.[code];
      const isMandatory = std.mandatory !== false;
      const blocksAudit = risk?.blocks_audit || false;
      
      if (isMandatory && blocksAudit) {
        band1.push({ ...std, risk });
      } else if (isMandatory) {
        band2.push({ ...std, risk });
      } else {
        band3.push({ ...std, risk });
      }
    });
    
    return { band1, band2, band3 };
  };
  
  const bands = categorizeStandards();
  
  const toggleBand = (band) => {
    setExpandedBands(prev => ({ ...prev, [band]: !prev[band] }));
  };
  
  const BandSection = ({ bandKey, title, items, color, icon: BandIcon, alwaysExpanded }) => {
    const isExpanded = expandedBands[bandKey];
    const trendingToMaterial = items.filter(s => s.risk?.risk_score > 0.6 && !s.risk?.blocks_audit).length;
    
    return (
      <div className={`rounded-xl border ${
        color === 'red' ? 'border-red-200 bg-red-50/50' :
        color === 'amber' ? 'border-amber-200 bg-amber-50/50' :
        'border-slate-200 bg-slate-50/50'
      }`}>
        <button
          onClick={() => !alwaysExpanded && toggleBand(bandKey)}
          className={`w-full px-4 py-3 flex items-center justify-between ${!alwaysExpanded ? 'cursor-pointer' : ''}`}
        >
          <div className="flex items-center gap-3">
            <BandIcon size={18} className={`${
              color === 'red' ? 'text-red-600' :
              color === 'amber' ? 'text-amber-600' :
              'text-slate-500'
            }`} />
            <span className={`font-semibold ${
              color === 'red' ? 'text-red-900' :
              color === 'amber' ? 'text-amber-900' :
              'text-slate-700'
            }`}>{title}</span>
            <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${
              color === 'red' ? 'bg-red-100 text-red-700' :
              color === 'amber' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-200 text-slate-600'
            }`}>
              {items.length} items
            </span>
            {trendingToMaterial > 0 && bandKey === 'band3' && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
                <Zap size={10} /> {trendingToMaterial} trending to material
              </span>
            )}
          </div>
          {!alwaysExpanded && (
            isExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />
          )}
        </button>
        
        {isExpanded && items.length > 0 && (
          <div className="px-4 pb-4 space-y-2">
            {items.map((std) => {
              const Icon = standardIcons[std.code] || Database;
              const ownerData = ownership?.[std.code?.replace(' ', '_')] || {};
              
              return (
                <button
                  key={std.code}
                  onClick={() => onTopicClick(std)}
                  className="w-full flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    color === 'red' ? 'bg-red-100' : color === 'amber' ? 'bg-amber-100' : 'bg-slate-100'
                  }`}>
                    <Icon size={16} className={`${
                      color === 'red' ? 'text-red-600' : color === 'amber' ? 'text-amber-600' : 'text-slate-500'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{std.code}</p>
                    <p className="text-xs text-slate-500 truncate">{std.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{ROLE_METADATA[ownerData.owner_role]?.label?.split(' ')[0] || 'Unassigned'}</span>
                    <span className={`w-2 h-2 rounded-full ${
                      std.risk?.risk_level === 'high' ? 'bg-red-500' :
                      std.risk?.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                    }`} />
                  </div>
                  <ArrowRight size={14} className="text-slate-300 group-hover:text-slate-500" />
                </button>
              );
            })}
          </div>
        )}
        
        {isExpanded && items.length === 0 && (
          <div className="px-4 pb-4">
            <p className="text-sm text-slate-500 text-center py-4">No items in this category</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Layers size={18} className="text-slate-600" />
        <h3 className="font-semibold text-slate-900">All Disclosures by Priority</h3>
      </div>
      
      <BandSection 
        bandKey="band1" 
        title="Mandatory & Blocking Audit" 
        items={bands.band1} 
        color="red"
        icon={Ban}
        alwaysExpanded={true}
      />
      <BandSection 
        bandKey="band2" 
        title="Mandatory but Non-Blocking" 
        items={bands.band2} 
        color="amber"
        icon={AlertTriangle}
      />
      <BandSection 
        bandKey="band3" 
        title="Voluntary" 
        items={bands.band3} 
        color="slate"
        icon={Circle}
      />
    </div>
  );
};

// ============================================================================
// PHASE 3: "Make Me Audit Ready" Button & Modal
// ============================================================================
const MakeMeAuditReadyModal = ({ isOpen, onClose, gapAnalysis, riskAssessment, ownership }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [auditPlan, setAuditPlan] = useState(null);
  
  const generateAuditPlan = () => {
    setIsGenerating(true);
    
    // Simulate AI generation (in real app, call backend)
    setTimeout(() => {
      const gaps = gapAnalysis?.gaps || {};
      const blockingItems = gaps.blocking_audit || [];
      const highPriorityItems = gaps.high_priority || [];
      
      // Calculate estimated weeks based on gaps
      const totalBlockers = blockingItems.length;
      const totalHigh = highPriorityItems.length;
      const estimatedWeeks = Math.max(4, totalBlockers * 2 + totalHigh);
      
      // Generate action sequence
      const actionSequence = [];
      
      // Week 1-2: Address blocking items first
      if (blockingItems.length > 0) {
        actionSequence.push({
          phase: 'Phase 1: Critical Blockers',
          weeks: '1-2',
          actions: blockingItems.slice(0, 3).map(item => ({
            standard: item.standard_code,
            action: item.missing_evidence > 0 ? 'Upload evidence documentation' : 'Complete data entry',
            owner: ROLE_METADATA[item.owner_role]?.label || 'Unassigned',
            priority: 'critical'
          }))
        });
      }
      
      // Week 3-4: High priority
      if (highPriorityItems.length > 0) {
        actionSequence.push({
          phase: 'Phase 2: High Priority',
          weeks: '3-4',
          actions: highPriorityItems.slice(0, 4).map(item => ({
            standard: item.standard_code,
            action: item.missing_claims > 0 ? 'Generate disclosure claims' : 'Complete evidence attachment',
            owner: ROLE_METADATA[item.owner_role]?.label || 'Unassigned',
            priority: 'high'
          }))
        });
      }
      
      // Week 5+: Review and approval
      actionSequence.push({
        phase: 'Phase 3: Review & Approval',
        weeks: `${Math.max(5, totalBlockers * 2)}-${estimatedWeeks}`,
        actions: [
          { standard: 'All Standards', action: 'Management review of all claims', owner: 'Compliance Manager', priority: 'medium' },
          { standard: 'All Standards', action: 'Legal counsel sign-off', owner: 'Legal Counsel', priority: 'medium' },
          { standard: 'All Standards', action: 'Generate audit pack', owner: 'Compliance Manager', priority: 'medium' }
        ]
      });
      
      setAuditPlan({
        estimatedWeeks,
        totalBlockers,
        totalHighPriority: highPriorityItems.length,
        requiredEvidence: blockingItems.reduce((acc, i) => acc + (i.missing_evidence || 0), 0) + highPriorityItems.reduce((acc, i) => acc + (i.missing_evidence || 0), 0),
        actionSequence
      });
      setIsGenerating(false);
    }, 1500);
  };
  
  // Export audit plan as CSV
  const handleExportPlan = () => {
    if (!auditPlan) return;
    
    let csvContent = "AUDIT READINESS ACTION PLAN\n";
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    csvContent += "SUMMARY\n";
    csvContent += `Estimated Weeks to Audit Ready,${auditPlan.estimatedWeeks}\n`;
    csvContent += `Total Blockers,${auditPlan.totalBlockers}\n`;
    csvContent += `High Priority Items,${auditPlan.totalHighPriority}\n`;
    csvContent += `Evidence Documents Needed,${auditPlan.requiredEvidence}\n\n`;
    
    csvContent += "ACTION SEQUENCE\n";
    csvContent += "Phase,Weeks,Standard,Action,Owner,Priority\n";
    
    auditPlan.actionSequence.forEach(phase => {
      phase.actions.forEach(action => {
        csvContent += `"${phase.phase}","${phase.weeks}","${action.standard}","${action.action}","${action.owner}","${action.priority}"\n`;
      });
    });
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `audit_readiness_plan_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Rocket size={24} />
              <div>
                <h2 className="text-xl font-bold">Make Me Audit Ready</h2>
                <p className="text-emerald-100 text-sm">AI-generated action plan to achieve compliance</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!auditPlan && !isGenerating && (
            <div className="text-center py-12">
              <Rocket size={48} className="mx-auto mb-4 text-emerald-500" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Ready to get audit-ready?</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Our AI will analyze your current gaps, prioritize actions, and create a step-by-step plan to achieve audit readiness.
              </p>
              <button
                onClick={generateAuditPlan}
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-2 mx-auto"
              >
                <Sparkles size={18} />
                Generate Audit Plan
              </button>
            </div>
          )}
          
          {isGenerating && (
            <div className="text-center py-12">
              <RefreshCw size={48} className="mx-auto mb-4 text-emerald-500 animate-spin" />
              <h3 className="text-lg font-semibold text-slate-900">Analyzing your compliance gaps...</h3>
              <p className="text-slate-500 text-sm mt-2">This may take a moment</p>
            </div>
          )}
          
          {auditPlan && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <Timer size={24} className="mx-auto mb-2 text-emerald-600" />
                  <p className="text-2xl font-bold text-emerald-700">{auditPlan.estimatedWeeks}</p>
                  <p className="text-xs text-emerald-600">Estimated Weeks</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <Ban size={24} className="mx-auto mb-2 text-red-600" />
                  <p className="text-2xl font-bold text-red-700">{auditPlan.totalBlockers}</p>
                  <p className="text-xs text-red-600">Blockers</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <AlertTriangle size={24} className="mx-auto mb-2 text-amber-600" />
                  <p className="text-2xl font-bold text-amber-700">{auditPlan.totalHighPriority}</p>
                  <p className="text-xs text-amber-600">High Priority</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <FileCheck size={24} className="mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-700">{auditPlan.requiredEvidence}</p>
                  <p className="text-xs text-blue-600">Evidence Needed</p>
                </div>
              </div>
              
              {/* Action Sequence */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <GitBranch size={18} /> Suggested Action Sequence
                </h4>
                <div className="space-y-4">
                  {auditPlan.actionSequence.map((phase, phaseIdx) => (
                    <div key={phaseIdx} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className={`px-4 py-3 flex items-center justify-between ${
                        phaseIdx === 0 ? 'bg-red-50' : phaseIdx === 1 ? 'bg-amber-50' : 'bg-slate-50'
                      }`}>
                        <span className="font-semibold text-slate-900">{phase.phase}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          phaseIdx === 0 ? 'bg-red-100 text-red-700' : phaseIdx === 1 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-700'
                        }`}>
                          Weeks {phase.weeks}
                        </span>
                      </div>
                      <div className="p-4 space-y-2">
                        {phase.actions.map((action, actionIdx) => (
                          <div key={actionIdx} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-slate-100">
                            <div className={`w-2 h-8 rounded-full ${
                              action.priority === 'critical' ? 'bg-red-500' : 
                              action.priority === 'high' ? 'bg-amber-500' : 'bg-slate-300'
                            }`} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">{action.action}</p>
                              <p className="text-xs text-slate-500">{action.standard}</p>
                            </div>
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{action.owner}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {auditPlan && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <button
              onClick={() => setAuditPlan(null)}
              className="px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              Regenerate
            </button>
            <div className="flex items-center gap-3">
              <button 
                onClick={handleExportPlan}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50"
                data-testid="export-plan-btn"
              >
                <Download size={16} className="inline mr-2" />
                Export Plan
              </button>
              <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                Start Executing
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 3: Disclosure Health Score Breakdown
// ============================================================================
const DisclosureHealthScore = ({ stats, pipelineStatus, evidence, claims }) => {
  // Calculate component scores
  const calculateHealthScores = () => {
    // Data Completeness (based on stats.dataCoverage)
    const dataCompleteness = stats.dataCoverage || 0;
    
    // Evidence Strength (Weak/Moderate/Strong)
    const totalEvidence = evidence?.length || 0;
    const verifiedEvidence = evidence?.filter(e => e.evidence_type === 'third_party_verification')?.length || 0;
    const evidenceStrength = verifiedEvidence > 3 ? 'Strong' : totalEvidence > 5 ? 'Moderate' : 'Weak';
    const evidenceScore = evidenceStrength === 'Strong' ? 90 : evidenceStrength === 'Moderate' ? 60 : 30;
    
    // Control Maturity (Low/Moderate/High)
    const approvedClaims = claims?.filter(c => c.status === 'approved' || c.status === 'locked')?.length || 0;
    const totalClaims = claims?.length || 0;
    const approvalRate = totalClaims > 0 ? (approvedClaims / totalClaims) * 100 : 0;
    const controlMaturity = approvalRate > 70 ? 'High' : approvalRate > 30 ? 'Moderate' : 'Low';
    const controlScore = controlMaturity === 'High' ? 85 : controlMaturity === 'Moderate' ? 55 : 25;
    
    // Recency (Poor/Good/Excellent)
    const recentEvidence = evidence?.filter(e => {
      const uploadDate = new Date(e.uploaded_at || e.created_at);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return uploadDate > thirtyDaysAgo;
    })?.length || 0;
    const recency = recentEvidence > 5 ? 'Excellent' : recentEvidence > 2 ? 'Good' : 'Poor';
    const recencyScore = recency === 'Excellent' ? 95 : recency === 'Good' ? 65 : 35;
    
    return {
      dataCompleteness,
      evidenceStrength,
      evidenceScore,
      controlMaturity,
      controlScore,
      recency,
      recencyScore
    };
  };
  
  const health = calculateHealthScores();
  
  const getScoreColor = (score) => {
    if (score >= 70) return 'emerald';
    if (score >= 40) return 'amber';
    return 'red';
  };
  
  const getLabelColor = (label) => {
    if (['Strong', 'High', 'Excellent'].includes(label)) return 'emerald';
    if (['Moderate', 'Good'].includes(label)) return 'amber';
    return 'red';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Activity size={18} className="text-emerald-600" />
        Disclosure Health Breakdown
      </h3>
      
      <div className="space-y-4">
        {/* Data Completeness */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-600">Data Completeness</span>
            <span className={`text-sm font-semibold text-${getScoreColor(health.dataCompleteness)}-600`}>
              {health.dataCompleteness}%
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-${getScoreColor(health.dataCompleteness)}-500 rounded-full transition-all`}
              style={{ width: `${health.dataCompleteness}%` }}
            />
          </div>
        </div>
        
        {/* Evidence Strength */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-600">Evidence Strength</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold bg-${getLabelColor(health.evidenceStrength)}-100 text-${getLabelColor(health.evidenceStrength)}-700`}>
              {health.evidenceStrength}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-${getScoreColor(health.evidenceScore)}-500 rounded-full transition-all`}
              style={{ width: `${health.evidenceScore}%` }}
            />
          </div>
        </div>
        
        {/* Control Maturity */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-600">Control Maturity</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold bg-${getLabelColor(health.controlMaturity)}-100 text-${getLabelColor(health.controlMaturity)}-700`}>
              {health.controlMaturity}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-${getScoreColor(health.controlScore)}-500 rounded-full transition-all`}
              style={{ width: `${health.controlScore}%` }}
            />
          </div>
        </div>
        
        {/* Recency */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-600">Data Recency</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold bg-${getLabelColor(health.recency)}-100 text-${getLabelColor(health.recency)}-700`}>
              {health.recency}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-${getScoreColor(health.recencyScore)}-500 rounded-full transition-all`}
              style={{ width: `${health.recencyScore}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Composite Score */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Composite Health Score</span>
          <div className="flex items-center gap-2">
            <Gauge size={16} className={`text-${getScoreColor(stats.overallScore)}-600`} />
            <span className={`text-lg font-bold text-${getScoreColor(stats.overallScore)}-600`}>
              {stats.overallScore}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 3: Audit View Toggle
// ============================================================================
const AuditViewToggle = ({ viewMode, onToggle }) => {
  return (
    <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
      <button
        onClick={() => onToggle('management')}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
          viewMode === 'management' 
            ? 'bg-white text-slate-900 shadow-sm' 
            : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        <BarChart3 size={14} />
        Management
      </button>
      <button
        onClick={() => onToggle('auditor')}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
          viewMode === 'auditor' 
            ? 'bg-white text-slate-900 shadow-sm' 
            : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        <Eye size={14} />
        Auditor
      </button>
    </div>
  );
};

// ============================================================================
// Topic Control Room (Detailed View)
// ============================================================================
const TopicControlRoom = ({ topic, onClose, savedValues, onSaveValue }) => {
  const [activeTab, setActiveTab] = useState('datapoints');
  const Icon = standardIcons[topic.code] || Database;
  const colors = categoryColors[topic.category] || categoryColors.general;
  
  // Calculate topic stats
  const totalDataPoints = topic.disclosures?.reduce((acc, d) => acc + (d.data_points?.length || 0), 0) || 0;
  const mandatoryCount = topic.disclosures?.reduce((acc, d) => 
    acc + (d.data_points?.filter(dp => dp.mandatory)?.length || 0), 0) || 0;
  const completedCount = savedValues?.length || 0;
  const readiness = totalDataPoints > 0 ? Math.round((completedCount / totalDataPoints) * 100) : 0;
  
  // Group data points by status
  const allDataPoints = topic.disclosures?.flatMap(d => d.data_points || []) || [];
  const readyDps = allDataPoints.filter(dp => savedValues?.some(v => v.data_point_id === dp.id && v.value));
  const missingDps = allDataPoints.filter(dp => !savedValues?.some(v => v.data_point_id === dp.id && v.value));
  const mandatoryMissing = missingDps.filter(dp => dp.mandatory);

  const tabs = [
    { id: 'datapoints', label: 'Data Points', count: totalDataPoints },
    { id: 'evidence', label: 'Evidence', count: 0 },
    { id: 'claims', label: 'Claims', count: 0 },
    { id: 'audit', label: 'Audit Trail', count: 0 }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header Strip */}
        <div className={`px-6 py-4 bg-gradient-to-r from-${colors.accent}-50 to-${colors.accent}-100 border-b border-${colors.accent}-200`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.bg}`}>
                <Icon size={24} className={colors.text} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{topic.code}: {topic.name}</h2>
                <p className="text-sm text-slate-600">Topic Control Room</p>
              </div>
              <button
                data-testid={`ai-draft-${topic.code}`}
                onClick={(e) => { e.stopPropagation(); generateESRSDraft(topic.code); }}
                disabled={aiDraftLoading}
                className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-200 transition-all border border-purple-200"
              >
                {aiDraftLoading ? 'Generating...' : 'AI Draft'}
              </button>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <XCircle size={24} className="text-slate-500" />
            </button>
          </div>
          
          {/* Stats Strip */}
          <div className="flex items-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className={`w-16 h-16 rounded-full border-4 ${
                readiness >= 80 ? 'border-emerald-400' : readiness >= 50 ? 'border-amber-400' : 'border-red-400'
              } flex items-center justify-center bg-white`}>
                <span className="text-lg font-bold">{readiness}%</span>
              </div>
              <span className="text-sm text-slate-600">Readiness</span>
            </div>
            <div className="h-8 w-px bg-slate-300" />
            <div>
              <span className="text-2xl font-bold text-red-600">{mandatoryMissing.length}</span>
              <span className="text-sm text-slate-600 ml-1">Mandatory gaps</span>
            </div>
            <div className="h-8 w-px bg-slate-300" />
            <div>
              <span className="text-2xl font-bold text-amber-600">{missingDps.length - mandatoryMissing.length}</span>
              <span className="text-sm text-slate-600 ml-1">Evidence missing</span>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 py-2 border-b border-slate-200 bg-slate-50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-white text-slate-900 shadow-sm border border-slate-200' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'datapoints' && (
            <div className="space-y-6">
              {/* Missing Mandatory - Urgent */}
              {mandatoryMissing.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Missing Mandatory ({mandatoryMissing.length})
                  </h3>
                  <div className="space-y-2">
                    {mandatoryMissing.map((dp, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-red-600">{dp.id}</span>
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">MANDATORY</span>
                        </div>
                        <p className="text-sm text-slate-800">{dp.description}</p>
                        <button className="mt-2 px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700">
                          Enter Data
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Needs Validation */}
              {(missingDps.length - mandatoryMissing.length) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Needs Validation ({missingDps.length - mandatoryMissing.length})
                  </h3>
                  <div className="space-y-2">
                    {missingDps.filter(dp => !dp.mandatory).slice(0, 5).map((dp, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-amber-600">{dp.id}</span>
                        </div>
                        <p className="text-sm text-slate-800">{dp.description}</p>
                        <button className="mt-2 px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700">
                          Enter Data
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Ready */}
              {readyDps.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Ready ({readyDps.length})
                  </h3>
                  <div className="space-y-2">
                    {readyDps.map((dp, idx) => {
                      const value = savedValues?.find(v => v.data_point_id === dp.id);
                      return (
                        <div key={idx} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-mono text-emerald-600">{dp.id}</span>
                            <CheckCircle2 size={14} className="text-emerald-600" />
                          </div>
                          <p className="text-sm text-slate-800">{dp.description}</p>
                          <p className="text-sm font-medium text-emerald-700 mt-1">Value: {value?.value}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'evidence' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Evidence Checklist</h3>
              {[
                { name: 'Policy document uploaded', status: 'complete' },
                { name: 'Transition plan', status: 'missing' },
                { name: 'Scenario analysis', status: 'in_progress' },
                { name: 'Target methodology', status: 'missing' },
                { name: 'Third-party verification', status: 'missing' }
              ].map((item, idx) => (
                <div key={idx} className={`p-3 rounded-lg border flex items-center justify-between ${
                  item.status === 'complete' ? 'bg-emerald-50 border-emerald-200' :
                  item.status === 'in_progress' ? 'bg-amber-50 border-amber-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-3">
                    {item.status === 'complete' ? <CheckCircle2 size={16} className="text-emerald-600" /> :
                     item.status === 'in_progress' ? <Clock size={16} className="text-amber-600" /> :
                     <XCircle size={16} className="text-red-600" />}
                    <span className="text-sm text-slate-800">{item.name}</span>
                  </div>
                  {item.status !== 'complete' && (
                    <button className="px-3 py-1 bg-slate-800 text-white rounded text-xs font-medium hover:bg-slate-900">
                      Upload
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {activeTab === 'claims' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Auto-Generated Claim Drafts</h3>
              <p className="text-sm text-slate-500 mb-4">
                These statements are auto-generated based on your data. Edit, approve, and lock for reporting.
              </p>
              {[
                { 
                  id: 'claim-1', 
                  text: `"The company emitted ${Math.floor(Math.random() * 50000)} tCO₂e in Scope 1 and 2 emissions for FY2024, representing a ${Math.floor(Math.random() * 20)}% reduction compared to the baseline year."`,
                  status: 'draft'
                },
                {
                  id: 'claim-2',
                  text: '"We have committed to achieving net-zero emissions by 2050, with interim targets validated by the Science Based Targets initiative."',
                  status: 'locked'
                }
              ].map((claim, idx) => (
                <div key={idx} className={`p-4 rounded-lg border ${
                  claim.status === 'locked' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs text-slate-500">Claim #{idx + 1}</span>
                    {claim.status === 'locked' ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <Lock size={12} /> Approved & Locked
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                        <Unlock size={12} /> Draft
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-800 italic">{claim.text}</p>
                  {claim.status !== 'locked' && (
                    <div className="flex items-center gap-2 mt-3">
                      <button className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded text-xs font-medium hover:bg-slate-200">
                        Edit
                      </button>
                      <button className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700">
                        Approve & Lock
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Audit Trail</h3>
              <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Action</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Source File</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">User</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {[
                      { action: 'Data entered', file: 'emissions_2024.xlsx', user: 'john@company.com', time: '2 hours ago' },
                      { action: 'Evidence uploaded', file: 'ghg_verification.pdf', user: 'sarah@company.com', time: '1 day ago' },
                      { action: 'Claim approved', file: '-', user: 'cfo@company.com', time: '3 days ago' }
                    ].map((entry, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-2 text-slate-800">{entry.action}</td>
                        <td className="px-4 py-2 text-slate-600">{entry.file}</td>
                        <td className="px-4 py-2 text-slate-600">{entry.user}</td>
                        <td className="px-4 py-2 text-slate-500">{entry.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Mandatory/Voluntary Toggle
// ============================================================================
const MandatoryToggle = ({ showMandatoryOnly, onToggle }) => {
  return (
    <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
      <button
        onClick={() => onToggle(true)}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          showMandatoryOnly ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        Mandatory Only
      </button>
      <button
        onClick={() => onToggle(false)}
        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          !showMandatoryOnly ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        All Disclosures
      </button>
    </div>
  );
};

// ============================================================================
// Resolve Missing Data Panel (Renamed from Quick Data Entry)
// ============================================================================
const ResolveMissingData = ({ missingDataPoints, onResolve }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filtered = missingDataPoints.filter(dp => 
    dp.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dp.description.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <AlertTriangle size={16} className="text-amber-600" />
        Resolve Missing Data
        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs ml-auto">
          {missingDataPoints.length} gaps
        </span>
      </h3>
      
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search missing data points..."
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
        />
      </div>

      <div className="max-h-64 overflow-y-auto space-y-2">
        {filtered.map((dp, idx) => (
          <button
            key={idx}
            onClick={() => onResolve(dp)}
            className="w-full p-3 rounded-lg border border-amber-200 bg-amber-50 text-left hover:border-amber-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-mono text-amber-700">{dp.id}</span>
              {dp.mandatory && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold">MANDATORY</span>
              )}
            </div>
            <p className="text-sm text-slate-800 line-clamp-2">{dp.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Perfect Flow Wizard - Gap to Closure
// ============================================================================
const PerfectFlowWizard = ({ dataPoint, standardCode, reportingPeriod, onComplete, onClose }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Evidence, 4: Claim, 5: Done
  const [uploadedFile, setUploadedFile] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [dataValue, setDataValue] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [generatedClaim, setGeneratedClaim] = useState('');
  const [loading, setLoading] = useState(false);
  
  const steps = [
    { id: 1, name: 'Upload Data', icon: Upload },
    { id: 2, name: 'Map to Standard', icon: Link2 },
    { id: 3, name: 'Attach Evidence', icon: FileCheck },
    { id: 4, name: 'Generate Claim', icon: FileText },
    { id: 5, name: 'Complete', icon: CheckCircle2 }
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    setLoading(true);

    const res = await apiFetch(`${API}/esrs/auto-map`, {
      method: 'POST',
      body: { file_name: file.name, detected_metrics: [] },
    });
    if (res?._error) {
      console.error('Auto-map failed:', res.message);
    } else {
      setMappings(res.detected_mappings || []);
    }
    setLoading(false);
  };

  const handleSaveData = async () => {
    if (!dataValue) return;
    
    setLoading(true);
    const res = await apiFetch(`${API}/esrs/data-values`, {
      method: 'POST',
      body: {
        data_points: [{
          data_point_id: dataPoint?.id || mappings[0]?.data_point_id,
          value: dataValue,
          reporting_period: reportingPeriod
        }]
      },
    });
    if (res?._error) {
      console.error('Save failed:', res.message);
    } else {
      setStep(3);
    }
    setLoading(false);
  };

  const handleEvidenceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setEvidenceFile(file);
    setLoading(true);

    const res = await apiFetch(`${API}/esrs/evidence`, {
      method: 'POST',
      body: {
        standard_code: standardCode,
        data_point_id: dataPoint?.id || mappings[0]?.data_point_id,
        evidence_type: 'supporting_document',
        file_name: file.name,
        reporting_period: reportingPeriod
      },
    });
    if (res?._error) {
      console.error('Evidence upload failed:', res.message);
    } else {
      setStep(4);
    }
    setLoading(false);
  };

  const handleGenerateClaim = async () => {
    setLoading(true);
    const res = await apiFetch(`${API}/esrs/generate-claim`, {
      method: 'POST',
      body: {
        standard_code: standardCode,
        data_point_ids: [dataPoint?.id || mappings[0]?.data_point_id],
        reporting_period: reportingPeriod
      },
    });
    if (res?._error) {
      console.error('Claim generation failed:', res.message);
      setGeneratedClaim(`[Draft claim for ${standardCode}] The company has reported data for this disclosure requirement.`);
    } else {
      setGeneratedClaim(res.generated_claim);
    }
    setLoading(false);
  };

  const handleSaveClaim = async () => {
    if (!generatedClaim) return;
    
    setLoading(true);
    const res = await apiFetch(`${API}/esrs/claims`, {
      method: 'POST',
      body: {
        standard_code: standardCode,
        data_point_ids: [dataPoint?.id || mappings[0]?.data_point_id],
        claim_text: generatedClaim,
        status: 'draft',
        reporting_period: reportingPeriod
      },
    });
    if (res?._error) {
      console.error('Save claim failed:', res.message);
    } else {
      setStep(5);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Zap size={20} />
                Perfect Flow: Gap to Closure
              </h2>
              <p className="text-emerald-100 text-sm mt-1">
                {dataPoint?.description || `Resolve gap for ${standardCode}`}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <XCircle size={20} />
            </button>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => {
              const Icon = s.icon;
              const isActive = s.id === step;
              const isComplete = s.id < step;
              
              return (
                <div key={s.id} className="flex items-center">
                  <div className={`flex items-center gap-2 ${
                    isComplete ? 'text-emerald-600' : isActive ? 'text-emerald-600' : 'text-slate-400'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-emerald-100' : isActive ? 'bg-emerald-600 text-white' : 'bg-slate-200'
                    }`}>
                      {isComplete ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                    </div>
                    <span className="text-xs font-medium hidden sm:inline">{s.name}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-2 ${isComplete ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Step 1: Upload Data File</h3>
              <p className="text-sm text-slate-600">
                Upload a file containing your data. We'll automatically detect relevant metrics.
              </p>
              
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors">
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".xlsx,.xls,.csv,.pdf"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload size={32} className="mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-700 font-medium">Click to upload or drag and drop</p>
                  <p className="text-xs text-slate-500 mt-1">Excel, CSV, or PDF files</p>
                </label>
              </div>
              
              {uploadedFile && (
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <FileCheck size={20} className="text-emerald-600" />
                  <span className="text-sm text-emerald-800">{uploadedFile.name}</span>
                  <CheckCircle2 size={16} className="text-emerald-600 ml-auto" />
                </div>
              )}
              
              <div className="flex justify-between pt-4">
                <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900">
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!uploadedFile}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Step 2: Map to ESRS Data Points</h3>
              <p className="text-sm text-slate-600">
                We detected the following potential mappings. Confirm or adjust.
              </p>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="animate-spin text-emerald-600" size={24} />
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {mappings.map((mapping, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{mapping.data_point_id}</p>
                        <p className="text-xs text-slate-600">{mapping.description}</p>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                        {Math.round(mapping.confidence * 100)}% match
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="pt-4">
                <label className="text-sm font-medium text-slate-700">Enter Value</label>
                <input
                  type="text"
                  value={dataValue}
                  onChange={(e) => setDataValue(e.target.value)}
                  placeholder="e.g., 15000 (tCO₂e)"
                  className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div className="flex justify-between pt-4">
                <button onClick={() => setStep(1)} className="px-4 py-2 text-slate-600 hover:text-slate-900">
                  Back
                </button>
                <button
                  onClick={handleSaveData}
                  disabled={!dataValue || loading}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
                  Save & Continue
                </button>
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Step 3: Attach Evidence</h3>
              <p className="text-sm text-slate-600">
                Upload supporting documentation to substantiate your data.
              </p>
              
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors">
                <input
                  type="file"
                  onChange={handleEvidenceUpload}
                  className="hidden"
                  id="evidence-upload"
                  accept=".pdf,.docx,.xlsx,.png,.jpg"
                />
                <label htmlFor="evidence-upload" className="cursor-pointer">
                  <FileCheck size={32} className="mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-700 font-medium">Upload Evidence Document</p>
                  <p className="text-xs text-slate-500 mt-1">Policy, verification report, or methodology</p>
                </label>
              </div>
              
              {evidenceFile && (
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <FileCheck size={20} className="text-emerald-600" />
                  <span className="text-sm text-emerald-800">{evidenceFile.name}</span>
                  <CheckCircle2 size={16} className="text-emerald-600 ml-auto" />
                </div>
              )}
              
              <div className="flex justify-between pt-4">
                <button onClick={() => setStep(2)} className="px-4 py-2 text-slate-600 hover:text-slate-900">
                  Back
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep(4)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900"
                  >
                    Skip for now
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    disabled={!evidenceFile}
                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Step 4: Generate Claim</h3>
              <p className="text-sm text-slate-600">
                Create a disclosure statement based on your data.
              </p>
              
              {!generatedClaim ? (
                <button
                  onClick={handleGenerateClaim}
                  disabled={loading}
                  className="w-full py-4 border-2 border-dashed border-emerald-300 rounded-xl text-emerald-600 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <RefreshCw className="animate-spin" size={20} /> : <Sparkles size={20} />}
                  Generate Claim with AI
                </button>
              ) : (
                <div className="space-y-3">
                  <textarea
                    value={generatedClaim}
                    onChange={(e) => setGeneratedClaim(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  />
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Info size={12} />
                    Edit the claim as needed before saving
                  </p>
                </div>
              )}
              
              <div className="flex justify-between pt-4">
                <button onClick={() => setStep(3)} className="px-4 py-2 text-slate-600 hover:text-slate-900">
                  Back
                </button>
                <button
                  onClick={handleSaveClaim}
                  disabled={!generatedClaim || loading}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <RefreshCw className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                  Save Claim & Complete
                </button>
              </div>
            </div>
          )}
          
          {step === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Gap Closed!</h3>
              <p className="text-slate-600 mb-6">
                You've successfully completed the disclosure workflow for this data point.
              </p>
              
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span className="text-slate-600">Data uploaded and mapped</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span className="text-slate-600">Value: {dataValue}</span>
                  </div>
                  {evidenceFile && (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-emerald-600" />
                      <span className="text-slate-600">Evidence: {evidenceFile.name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span className="text-slate-600">Claim draft created</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  onComplete();
                  onClose();
                }}
                className="px-8 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================
const ESRSDataPoints = () => {
  const [standards, setStandards] = useState([]);
  const [allDataPoints, setAllDataPoints] = useState([]);
  const [savedValues, setSavedValues] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [claims, setClaims] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [reportingPeriod, setReportingPeriod] = useState(new Date().getFullYear().toString());
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showMandatoryOnly, setShowMandatoryOnly] = useState(true);
  const [showPerfectFlow, setShowPerfectFlow] = useState(false);
  const [selectedGap, setSelectedGap] = useState(null);
  
  // New Phase 1 state
  const [riskAssessment, setRiskAssessment] = useState({});
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [claimsBreakdown, setClaimsBreakdown] = useState(null);
  const [ownership, setOwnership] = useState({});
  
  // Phase 2 & 3 state
  const [stageDrawer, setStageDrawer] = useState({ isOpen: false, topic: null, stage: null });
  const [showAuditReadyModal, setShowAuditReadyModal] = useState(false);
  const [viewMode, setViewMode] = useState('management'); // 'management' or 'auditor'
  
  // AI Intelligence Layer hooks
  const aiQuality = useAIQuality("csrd_esrs");
  const aiDraftHook = useAIDraft("csrd_esrs");
  const [aiQualityChecks, setAiQualityChecks] = useState({});
  const [aiDraftResult, setAiDraftResult] = useState(null);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);

  const runAIQualityCheck = async (dpId, value, unit) => {
    try {
      const res = await aiQuality.check({ datapoint_id: dpId, value, unit });
      setAiQualityChecks(p => ({...p, [dpId]: res?.result?.quality_score || 0}));
    } catch {}
  };

  const generateESRSDraft = async (topicId) => {
    setAiDraftLoading(true);
    try {
      const dpIds = allDataPoints.filter(dp => dp.id?.startsWith(topicId?.split(' ').pop())).map(dp => dp.id).slice(0, 5);
      const res = await aiDraftHook.generate({ framework_id: 'ESRS', section_id: topicId, datapoint_ids: dpIds });
      setAiDraftResult(res);
    } catch {} finally { setAiDraftLoading(false); }
  };
  
  // Computed stats
  const [stats, setStats] = useState({
    overallScore: 0,
    dataCoverage: 0,
    evidenceCoverage: 0,
    claimsDefensible: 0,
    auditReady: false
  });
  
  const [gaps, setGaps] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [timeToReady, setTimeToReady] = useState({ weeks: 0, blocker: '' });

  useEffect(() => {
    loadData();
  }, [reportingPeriod]);

  const loadData = async () => {
    setLoading(true);
    try {
      // apiFetch attaches auth + returns the payload directly (or {_error});
      // wrap each back into {data} with the same per-call fallback the old
      // axios .catch() blocks provided, so downstream .data access is unchanged
      const get = (path, fallback) =>
        apiFetch(`${API}${path}`).then((d) => ({ data: d && !d._error ? d : fallback }));

      // Fetch all data including new Phase 1 endpoints
      const [
        standardsRes,
        dataPointsRes,
        valuesRes,
        evidenceRes,
        claimsRes,
        pipelineRes,
        riskRes,
        gapRes,
        claimsBreakdownRes,
        ownershipRes
      ] = await Promise.all([
        get('/esrs/standards', { standards: [] }),
        get('/esrs/data-points', { data_points: [] }),
        get(`/esrs/data-values?reporting_period=${reportingPeriod}`, { values: [] }),
        get(`/esrs/evidence?reporting_period=${reportingPeriod}`, { evidence: [] }),
        get(`/esrs/claims?reporting_period=${reportingPeriod}`, { claims: [] }),
        get(`/esrs/pipeline-status?reporting_period=${reportingPeriod}`, { pipeline_status: {}, overall_readiness: 0 }),
        get(`/esrs/risk-assessment?reporting_period=${reportingPeriod}`, { risk_assessments: {}, summary: {} }),
        get(`/esrs/gap-analysis?reporting_period=${reportingPeriod}`, { gaps: {}, summary: {} }),
        get(`/esrs/claims-breakdown?reporting_period=${reportingPeriod}`, { breakdown: {}, defensibility: {} }),
        get(`/esrs/topic-ownership?reporting_period=${reportingPeriod}`, { ownership: {} })
      ]);

      const pipelineData = pipelineRes.data.pipeline_status || {};
      
      // Store new Phase 1 data
      setRiskAssessment(riskRes.data.risk_assessments || {});
      setGapAnalysis(gapRes.data);
      setClaimsBreakdown(claimsBreakdownRes.data);
      setOwnership(ownershipRes.data.ownership || {});
      
      // Handle standards - API returns array, normalize to handle both formats
      const standardsList = Array.isArray(standardsRes.data.standards) 
        ? standardsRes.data.standards 
        : Object.entries(standardsRes.data.standards || {}).map(([code, data]) => ({ code, ...data }));
      
      // Merge standards with real pipeline status
      const standardsArray = standardsList.map((std) => {
        const code = std.code || std.id;
        const status = pipelineData[code] || pipelineData[code.replace(' ', '_')] || {};
        return {
          code,
          name: std.name,
          category: std.category,
          description: std.description,
          disclosures: std.disclosures,
          stages: status.stages || {
            upload: 'not_started',
            map: 'not_started',
            evidence: 'not_started',
            claim: 'not_started',
            audit: 'not_started'
          },
          readiness: status.readiness || 0
        };
      });
      
      setStandards(standardsArray);
      setAllDataPoints(dataPointsRes.data.data_points || []);
      setSavedValues(valuesRes.data.values || []);
      setEvidence(evidenceRes.data.evidence || []);
      setClaims(claimsRes.data.claims || []);
      setPipelineStatus(pipelineData);
      
      // Calculate stats from real data
      const totalDps = dataPointsRes.data.data_points?.length || 0;
      const completedDps = valuesRes.data.values?.length || 0;
      const totalEvidence = evidenceRes.data.evidence?.length || 0;
      const totalClaims = claimsRes.data.claims?.length || 0;
      const approvedClaims = (claimsRes.data.claims || []).filter(c => c.status === 'approved' || c.status === 'locked').length;
      
      const dataCoverage = totalDps > 0 ? Math.round((completedDps / totalDps) * 100) : 0;
      const evidenceCoverage = totalDps > 0 ? Math.round((totalEvidence / (totalDps * 0.3)) * 100) : 0; // ~30% of DPs need evidence
      const claimsDefensible = claimsBreakdownRes.data?.defensibility?.percentage || 
        (standardsArray.length > 0 ? Math.round((approvedClaims / standardsArray.length) * 100) : 0);
      const overallScore = pipelineRes.data.overall_readiness || Math.round((dataCoverage * 0.4 + Math.min(evidenceCoverage, 100) * 0.3 + claimsDefensible * 0.3));
      
      setStats({
        overallScore,
        dataCoverage,
        evidenceCoverage: Math.min(evidenceCoverage, 100),
        claimsDefensible,
        auditReady: overallScore >= 80
      });
      
      // Calculate gaps from real data
      const mandatoryDps = dataPointsRes.data.data_points?.filter(dp => dp.mandatory) || [];
      const missingMandatory = mandatoryDps.filter(dp => 
        !valuesRes.data.values?.some(v => v.data_point_id === dp.id)
      );
      
      const gapItems = [];
      
      if (missingMandatory.length > 0) {
        gapItems.push({
          title: `${missingMandatory.length} mandatory datapoints missing`,
          description: 'Critical compliance requirement',
          priority: 'critical',
          type: 'mandatory',
          dataPoints: missingMandatory.slice(0, 5)
        });
      }
      
      // Check for standards without evidence
      const standardsWithoutEvidence = standardsArray.filter(s => 
        !(evidenceRes.data.evidence || []).some(e => e.standard_code === s.code)
      );
      if (standardsWithoutEvidence.length > 0) {
        gapItems.push({
          title: `${standardsWithoutEvidence.length} standards missing evidence`,
          description: 'Upload supporting documentation',
          priority: 'high',
          type: 'evidence',
          standards: standardsWithoutEvidence.slice(0, 3).map(s => s.code)
        });
      }
      
      // Check for standards without claims
      const standardsWithoutClaims = standardsArray.filter(s => 
        !(claimsRes.data.claims || []).some(c => c.standard_code === s.code)
      );
      if (standardsWithoutClaims.length > 0) {
        gapItems.push({
          title: `${standardsWithoutClaims.length} standards need disclosure statements`,
          description: 'Generate claims for completed data',
          priority: 'high',
          type: 'claims',
          standards: standardsWithoutClaims.slice(0, 3).map(s => s.code)
        });
      }
      
      setGaps(gapItems);
      
      // Set time to ready
      const blockerPriority = missingMandatory.length > 0 ? 'Mandatory data points' : 
                             standardsWithoutEvidence.length > 0 ? 'Evidence documentation' : 'Claim approvals';
      setTimeToReady({
        weeks: Math.max(4, Math.round((100 - overallScore) / 5)),
        blocker: blockerPriority
      });
      
      // Set suggestions based on uploaded files
      if (completedDps > 0 && standardsWithoutClaims.length > 0) {
        setSuggestions([{
          message: `You have data for ${completedDps} data points but no claims generated.`,
          detail: 'Generate disclosure statements automatically?',
          action: 'generate_claims',
          standardCode: standardsWithoutClaims[0]?.code
        }]);
      }
      
    } catch (err) {
      console.error('Failed to load ESRS data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGapClick = (gap) => {
    // Open the Perfect Flow wizard for this gap
    if (gap.dataPoints && gap.dataPoints.length > 0) {
      setSelectedGap({
        dataPoint: gap.dataPoints[0],
        standardCode: gap.dataPoints[0]?.id?.split('-')[0] || 'ESRS E1'
      });
      setShowPerfectFlow(true);
    } else if (gap.standards && gap.standards.length > 0) {
      setSelectedGap({
        dataPoint: null,
        standardCode: gap.standards[0]
      });
      setShowPerfectFlow(true);
    }
  };
  
  const handleTopicClick = (topic) => {
    setSelectedTopic(topic);
  };
  
  const handleAcceptSuggestion = async (suggestion) => {
    if (suggestion.action === 'generate_claims') {
      // Open perfect flow for claim generation
      setSelectedGap({
        dataPoint: null,
        standardCode: suggestion.standardCode
      });
      setShowPerfectFlow(true);
    }
    setSuggestions(prev => prev.filter(s => s !== suggestion));
  };
  
  const handleDismissSuggestion = (suggestion) => {
    setSuggestions(prev => prev.filter(s => s !== suggestion));
  };
  
  const handleResolveMissingData = (dp) => {
    setSelectedGap({
      dataPoint: dp,
      standardCode: dp.id?.split('-')[0] || 'ESRS'
    });
    setShowPerfectFlow(true);
  };
  
  // Export functionality for Disclosure Workbench
  const handleExportData = () => {
    // Prepare export data
    const exportData = {
      reportingPeriod,
      generatedAt: new Date().toISOString(),
      stats: {
        overallReadiness: stats.overallScore,
        dataCoverage: stats.dataCoverage,
        evidenceCoverage: stats.evidenceCoverage,
        claimsDefensible: stats.claimsDefensible,
        auditReady: stats.auditReady
      },
      standards: standards.map(s => ({
        code: s.code,
        name: s.name,
        category: s.category,
        readiness: s.readiness || 0,
        stages: s.stages
      })),
      gapSummary: gapAnalysis?.summary || {},
      evidence: evidence?.length || 0,
      claims: claims?.length || 0
    };
    
    // Create CSV content
    let csvContent = "DISCLOSURE WORKBENCH EXPORT\n";
    csvContent += `Generated: ${new Date().toLocaleString()}\n`;
    csvContent += `Reporting Period: FY ${reportingPeriod}\n\n`;
    
    csvContent += "READINESS SUMMARY\n";
    csvContent += `Overall Score,${stats.overallScore}%\n`;
    csvContent += `Data Coverage,${stats.dataCoverage}%\n`;
    csvContent += `Evidence Coverage,${stats.evidenceCoverage}%\n`;
    csvContent += `Claims Defensible,${stats.claimsDefensible}%\n`;
    csvContent += `Audit Ready,${stats.auditReady ? 'Yes' : 'No'}\n\n`;
    
    csvContent += "GAP ANALYSIS\n";
    csvContent += `Blocking Audit,${gapAnalysis?.summary?.blocking_audit || 0}\n`;
    csvContent += `High Priority,${gapAnalysis?.summary?.high_priority || 0}\n`;
    csvContent += `Medium Priority,${gapAnalysis?.summary?.medium_priority || 0}\n`;
    csvContent += `Low Priority,${gapAnalysis?.summary?.low_priority || 0}\n\n`;
    
    csvContent += "STANDARDS STATUS\n";
    csvContent += "Code,Name,Category,Readiness,Upload,Map,Evidence,Claim,Audit\n";
    standards.forEach(s => {
      csvContent += `${s.code},${s.name || ''},${s.category || ''},${s.readiness || 0}%,`;
      csvContent += `${s.stages?.upload || 'not_started'},${s.stages?.map || 'not_started'},`;
      csvContent += `${s.stages?.evidence || 'not_started'},${s.stages?.claim || 'not_started'},`;
      csvContent += `${s.stages?.audit || 'not_started'}\n`;
    });
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `disclosure_workbench_export_${reportingPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handlePerfectFlowComplete = () => {
    // Refresh data after completing the flow
    loadData();
  };
  
  // Phase 2 & 3: Stage Drawer Handler
  const handleStageClick = (topic, stage) => {
    setStageDrawer({ isOpen: true, topic, stage });
  };
  
  const handleStageDrawerAction = (action, topic, stage) => {
    if (action === 'resolve') {
      setStageDrawer({ isOpen: false, topic: null, stage: null });
      setSelectedGap({
        dataPoint: null,
        standardCode: topic.code
      });
      setShowPerfectFlow(true);
    }
  };

  // Get missing data points
  const missingDataPoints = allDataPoints.filter(dp => 
    !savedValues.some(v => v.data_point_id === dp.id && v.value)
  );

  // Filter standards by mandatory
  const filteredStandards = showMandatoryOnly 
    ? standards.filter(s => s.mandatory !== false) // Show all by default, filter only explicit non-mandatory
    : standards;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Phase 3 features */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Database size={28} className="text-emerald-600" />
            Disclosure Workbench
          </h1>
          <p className="text-slate-600 mt-1">Your path from data to audit-ready disclosure</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Audit View Toggle */}
          <AuditViewToggle viewMode={viewMode} onToggle={setViewMode} />
          
          <select
            value={reportingPeriod}
            onChange={(e) => setReportingPeriod(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="2024">FY 2024</option>
            <option value="2025">FY 2025</option>
            <option value="2026">FY 2026</option>
          </select>
          
          {/* Make Me Audit Ready Button */}
          <button 
            onClick={() => setShowAuditReadyModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:from-emerald-600 hover:to-teal-600 flex items-center gap-2 shadow-lg shadow-emerald-500/25"
          >
            <Rocket size={16} />
            Make Me Audit Ready
          </button>
          
          <button 
            onClick={handleExportData}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2"
            data-testid="export-workbench-btn"
          >
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Layer 1: Executive Readiness Bar */}
      <ExecutiveReadinessBar 
        stats={stats} 
        timeToReady={timeToReady} 
        claimsBreakdown={claimsBreakdown}
        onMetricClick={(key) => console.log('Metric clicked:', key)}
      />

      {/* View Mode Indicator Banner */}
      {viewMode === 'auditor' && (
        <div className="bg-slate-800 text-white px-4 py-2 rounded-lg flex items-center gap-3">
          <Eye size={18} className="text-slate-300" />
          <span className="text-sm font-medium">Auditor View Active</span>
          <span className="text-xs text-slate-400">— Showing evidence traceability, approval trails, and audit metrics. Internal notes hidden.</span>
        </div>
      )}

      {/* Management View: Gap Engine & Autopilot Suggestions */}
      {viewMode === 'management' && (
        <>
          {/* Layer 2: Action-Oriented Gap Engine with Segmentation */}
          <GapEngine gapAnalysis={gapAnalysis} onGapClick={handleGapClick} />

          {/* Layer 4: Autopilot Suggestions */}
          <AutopilotSuggestions 
            suggestions={suggestions} 
            onAccept={handleAcceptSuggestion}
            onDismiss={handleDismissSuggestion}
          />
        </>
      )}

      {/* Auditor View: Disclosure Health Score & Audit Trail (Top Position) */}
      {viewMode === 'auditor' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DisclosureHealthScore
            stats={stats}
            pipelineStatus={pipelineStatus}
            evidence={evidence}
            claims={claims}
          />
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <History size={18} className="text-slate-600" />
              Audit Trail Summary
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Evidence Documents</span>
                <span className="font-bold text-lg text-slate-900">{evidence?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Approved Claims</span>
                <span className="font-bold text-lg text-emerald-600">{claims?.filter(c => c.status === 'approved')?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Third-party Verifications</span>
                <span className="font-bold text-lg text-blue-600">{evidence?.filter(e => e.evidence_type === 'third_party_verification')?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Pending Approvals</span>
                <span className="font-bold text-lg text-amber-600">{claims?.filter(c => c.status === 'pending_approval')?.length || 0}</span>
              </div>
              <div className="border-t border-slate-200 pt-3 mt-3">
                <p className="text-xs text-slate-500">
                  Last audit sync: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Layer 3: Operational Pipeline View with Ownership & Risk */}
      <PipelineView 
        topics={filteredStandards} 
        riskAssessment={riskAssessment}
        ownership={ownership}
        onTopicClick={handleTopicClick}
        onStageClick={handleStageClick}
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Mandatory Toggle + Standards Summary */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Disclosure Standards</h2>
            <MandatoryToggle showMandatoryOnly={showMandatoryOnly} onToggle={setShowMandatoryOnly} />
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm text-slate-600 mb-2">Mandatory Progress</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900">
                  {savedValues.filter(v => allDataPoints.find(dp => dp.id === v.data_point_id)?.mandatory).length}
                </span>
                <span className="text-slate-500">/ {allDataPoints.filter(dp => dp.mandatory).length}</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${(savedValues.filter(v => allDataPoints.find(dp => dp.id === v.data_point_id)?.mandatory).length / Math.max(1, allDataPoints.filter(dp => dp.mandatory).length)) * 100}%` }}
                />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm text-slate-600 mb-2">Voluntary Progress</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900">
                  {savedValues.filter(v => !allDataPoints.find(dp => dp.id === v.data_point_id)?.mandatory).length}
                </span>
                <span className="text-slate-500">/ {allDataPoints.filter(dp => !dp.mandatory).length}</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${(savedValues.filter(v => !allDataPoints.find(dp => dp.id === v.data_point_id)?.mandatory).length / Math.max(1, allDataPoints.filter(dp => !dp.mandatory).length)) * 100}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* High Risk Areas */}
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <AlertTriangle size={16} />
              High Risk Areas
            </h3>
            <div className="flex flex-wrap gap-2">
              {['Scope 3', 'Biodiversity', 'Value Chain Workers'].map((area, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium flex items-center gap-1">
                  <AlertTriangle size={12} />
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right: Resolve Missing Data */}
        <div>
          <ResolveMissingData 
            missingDataPoints={missingDataPoints}
            onResolve={handleResolveMissingData}
          />
        </div>
      </div>

      {/* 3-Band Split View for All Disclosures (when not filtered) */}
      {!showMandatoryOnly && (
        <ThreeBandView
          standards={standards}
          riskAssessment={riskAssessment}
          ownership={ownership}
          onTopicClick={handleTopicClick}
        />
      )}

      {/* Topic Control Room Modal */}
      {selectedTopic && (
        <TopicControlRoom
          topic={selectedTopic}
          onClose={() => setSelectedTopic(null)}
          savedValues={savedValues.filter(v => 
            selectedTopic.disclosures?.some(d => 
              d.data_points?.some(dp => dp.id === v.data_point_id)
            )
          )}
          onSaveValue={(dpId, value) => console.log('Save:', dpId, value)}
        />
      )}
      
      {/* Perfect Flow Wizard Modal */}
      {showPerfectFlow && selectedGap && (
        <PerfectFlowWizard
          dataPoint={selectedGap.dataPoint}
          standardCode={selectedGap.standardCode}
          reportingPeriod={reportingPeriod}
          onComplete={handlePerfectFlowComplete}
          onClose={() => {
            setShowPerfectFlow(false);
            setSelectedGap(null);
          }}
        />
      )}
      
      {/* Stage Drawer (Clickable Pipeline Stages) */}
      <StageDrawer
        isOpen={stageDrawer.isOpen}
        onClose={() => setStageDrawer({ isOpen: false, topic: null, stage: null })}
        topic={stageDrawer.topic}
        stage={stageDrawer.stage}
        riskData={riskAssessment}
        ownership={ownership}
        evidence={evidence}
        claims={claims}
        onAction={handleStageDrawerAction}
      />
      
      {/* Make Me Audit Ready Modal */}
      <MakeMeAuditReadyModal
        isOpen={showAuditReadyModal}
        onClose={() => setShowAuditReadyModal(false)}
        gapAnalysis={gapAnalysis}
        riskAssessment={riskAssessment}
        ownership={ownership}
      />
    </div>
  );
};

export default ESRSDataPoints;
